import { useEffect, useState } from "react";
import {
  Table,
  Title,
  Button,
  Container,
  Pagination,
  Loader,
  Center,
  Code,
  Text,
  NumberFormatter,
} from "@mantine/core";
import { Link, useLocation } from "wouter";
import { useTenantStore } from "./tenantStore";
import { getTenantsCredits } from "../../api";

// Below this balance (1¢ == 10,000 credits) the credit gate rejects the tenant's requests.
const CREDIT_GATE_THRESHOLD = 10000;

function TenantList() {
  const [, setLocation] = useLocation();

  const allTenants = useTenantStore((state) => state.allTenants);
  const fetchTenants = useTenantStore((state) => state.fetchTenants);

  const [activePage, setActivePage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  // Credits are loaded asynchronously after the list renders, keyed by tenant id.
  const [credits, setCredits] = useState({});
  const [creditsLoading, setCreditsLoading] = useState(false);

  const pageSize = 50;

  useEffect(() => {
    setIsLoading(true);
    setCredits({});
    setCreditsLoading(true);
    fetchTenants(activePage, pageSize)
      .then((data) => {
        const ids = (data?.data ?? []).map((t) => t.id);
        if (ids.length === 0) {
          setCreditsLoading(false);
          return undefined;
        }
        return getTenantsCredits(ids)
          .then((map) => setCredits(map ?? {}))
          .finally(() => setCreditsLoading(false));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [activePage, fetchTenants]);

  if (!allTenants) {
    return (
      <Center mt="xl">
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <Container size="md">
      <Title order={1}>Tenants</Title>
      <Button component={Link} to="/tenants/create" mt="xl" mb="xl">
        New Tenant
      </Button>

      <Pagination
        hideWithOnePage={false}
        total={Math.max(1, Math.ceil(allTenants.totalCount / pageSize))}
        value={activePage}
        onChange={setActivePage}
      />
      {isLoading ? <Loader size="sm" /> : null}

      <Table highlightOnHover stickyHeader stickyHeaderOffset={60}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Id</Table.Th>
            <Table.Th>Name</Table.Th>
            <Table.Th>Plan</Table.Th>
            <Table.Th ta="right">Credits</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {allTenants.data?.map((p) => (
            <Table.Tr
              key={p.id}
              onClick={() => setLocation(`tenants/${p.id}`)}
              style={{ cursor: "pointer" }}
            >
              <Table.Td>
                <Code fz="sm">{p.id}</Code>
              </Table.Td>
              <Table.Td>{p.name}</Table.Td>
              <Table.Td>{p.plan}</Table.Td>
              <Table.Td ta="right">
                {creditsLoading ? (
                  <Loader size="xs" />
                ) : credits[p.id] === undefined ? (
                  <Text size="xs" c="dimmed">
                    -
                  </Text>
                ) : (
                  <Text
                    size="sm"
                    c={credits[p.id] < CREDIT_GATE_THRESHOLD ? "red" : undefined}
                    fw={credits[p.id] < CREDIT_GATE_THRESHOLD ? 600 : undefined}
                  >
                    <NumberFormatter value={credits[p.id]} thousandSeparator />
                  </Text>
                )}
              </Table.Td>
              <Table.Td>{p.created}</Table.Td>
              <Table.Td>
                <Button
                  size="compact-sm"
                  onClick={(evt) => {
                    evt.stopPropagation();
                    setLocation(`/tenants/${p.id}/users/create`);
                  }}
                >
                  + User
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Container>
  );
}

export default TenantList;
